import type { paramsTypeAllRequired } from '~~/modules/api/rurality/v3/search/cottages/paramsSchema'
import { useFacetParams } from '~~/modules/views/cottage-list/composables/useFacetParams'

export interface CottageListFilters {
  f?: string
  l?: string
  minPrice?: number
  maxPrice?: number
  dateIn?: string
  dateOut?: string
  c?: number
  a?: string
  cor?: string
  ec?: number
  r?: string
  bo?: number
  of?: number
  sort?: string
  q?: string
  ne?: string
  sw?: string
}

export function useCottageListFilters() {
  const route = useRoute()
  const { getFacetParams } = useFacetParams()

  /**
   * Parse URL query parameters into filter object
   */
  function parseUrlParams(): paramsTypeAllRequired {
    const query = route.query
    return {
      page: query.page ? Number(query.page) : 1,
      l: (query.l as string) || undefined,
      f: (query.f as string) || undefined,
      min_price: query.min_price ? Number(query.min_price) : undefined,
      max_price: query.max_price ? Number(query.max_price) : undefined,
      d: (query.d as string) || undefined,
      c: query.c ? Number(query.c) : undefined,
      a: (query.a as string) || undefined,
      cor: (query.cor as string) || undefined,
      ec: query.ec ? (1 as const) : undefined,
      r: (query.r as string) || undefined,
      bo: query.bo ? (1 as const) : undefined,
      of: query.of ? (1 as const) : undefined,
      sort: (query.sort as string) || undefined,
      q: (query.q as string) || undefined,
      ne: (query.ne as string) || undefined,
      sw: (query.sw as string) || undefined,
    }
  }

  /**
   * Build URL query object from filters
   */
  function buildUrlQuery(
    filters: CottageListFilters,
    page?: number,
    currentPage?: number,
  ): Record<string, string> {
    const query: Record<string, string> = {}

    const routeSlug = route.params.slug as string | undefined
    const locationValue = filters.l || routeSlug || 'espana'
    if (locationValue) query.l = locationValue

    if (filters.f) query.f = filters.f
    if (filters.minPrice !== undefined)
      query.min_price = String(filters.minPrice)
    if (filters.maxPrice !== undefined)
      query.max_price = String(filters.maxPrice)
    if (filters.dateIn && filters.dateOut) {
      query.d = `${filters.dateIn},${filters.dateOut}`
    }
    if (filters.c !== undefined) query.c = String(filters.c)
    if (filters.a) query.a = filters.a

    const routeCorner = route.params.corner as string | undefined
    const cornerValue = filters.cor || routeCorner
    if (cornerValue) query.cor = cornerValue
    if (filters.ec !== undefined) query.ec = String(filters.ec)
    if (filters.r !== undefined) query.r = filters.r
    if (filters.bo !== undefined) query.bo = String(filters.bo)
    if (filters.of !== undefined) query.of = String(filters.of)
    if (filters.sort) query.sort = filters.sort
    if (filters.q) query.q = filters.q
    if (filters.ne) query.ne = filters.ne
    if (filters.sw) query.sw = filters.sw

    const pageNumber = page || currentPage || 1
    if (pageNumber > 1) query.page = String(pageNumber)

    // Preserve viz parameter if it exists
    if (route.query.viz === 'map') {
      query.viz = 'map'
    }

    return query
  }

  /**
   * Navigate to results page with filters
   */
  function navigateWithFilters(
    filters: CottageListFilters,
    page?: number,
    currentPage?: number,
  ) {
    const query = buildUrlQuery(filters, page, currentPage)

    const currentPath = route.path
    const shouldPreservePath = currentPath.startsWith('/casas-rurales/')

    navigateTo(
      {
        path: shouldPreservePath ? currentPath : routes().cottageList(),
        query,
      },
      { external: true },
    )
  }

  /**
   * Get current filters from URL
   */
  function getCurrentFilters(): CottageListFilters {
    const facetParams = getFacetParams()
    const params = facetParams || parseUrlParams()
    const dates = params.d ? params.d.split(',') : []
    return {
      l: params.l,
      f: params.f,
      minPrice: params.min_price,
      maxPrice: params.max_price,
      dateIn: dates[0] || undefined,
      dateOut: dates[1] || undefined,
      c: params.c,
      a: params.a,
      cor: params.cor,
      ec: params.ec,
      r: params.r,
      bo: params.bo,
      of: params.of,
      sort: params.sort,
      q: params.q,
      ne: params.ne,
      sw: params.sw,
    }
  }

  /**
   * Handle location change
   */
  function handleLocationChange(location: string) {
    const currentFilters = getCurrentFilters()
    navigateWithFilters(
      {
        ...currentFilters,
        l: location,
      },
      1,
    )
  }

  /**
   * Handle online booking filter toggle
   */
  function handleOnlineBookingFilter() {
    const currentFilters = getCurrentFilters()
    navigateWithFilters(
      {
        ...currentFilters,
        bo: 1,
      },
      1,
    )
  }

  /**
   * Handle exact capacity filter toggle
   */
  function handleExactCapacityFilter() {
    const currentFilters = getCurrentFilters()
    navigateWithFilters(
      {
        ...currentFilters,
        ec: 1,
      },
      1,
    )
  }

  /**
   * Remove a specific filter by ID
   */
  function handleRemoveFilter(filterId: number) {
    const currentFilters = getCurrentFilters()
    const filtersArray = currentFilters.f ? currentFilters.f.split(',') : []

    const updatedFilters = filtersArray.filter(f => f !== String(filterId))

    navigateWithFilters(
      {
        ...currentFilters,
        f: updatedFilters.length > 0 ? updatedFilters.join(',') : undefined,
      },
      1,
    )
  }

  /**
   * Update filters and navigate
   * Preserves all URL parameters that are not provided in the filters object
   */
  function updateFilters(filters: CottageListFilters, page?: number) {
    const currentUrlParams = getCurrentFilters()
    const mergedFilters = { ...currentUrlParams, ...filters }
    navigateWithFilters(mergedFilters, page)
  }

  function hasFiltersApplied(): boolean {
    const currentFilters = getCurrentFilters()
    return (
      !!currentFilters.f ||
      !!currentFilters.minPrice ||
      !!currentFilters.maxPrice ||
      !!currentFilters.dateIn ||
      !!currentFilters.dateOut ||
      !!currentFilters.c ||
      !!currentFilters.a ||
      !!currentFilters.ec ||
      !!currentFilters.r ||
      !!currentFilters.bo ||
      !!currentFilters.of ||
      !!currentFilters.sort ||
      !!currentFilters.q
    )
  }

  function handlePageFilter(page: number) {
    const currentFilters = getCurrentFilters()
    navigateWithFilters(currentFilters, page)
  }

  return {
    parseUrlParams,
    buildUrlQuery,
    navigateWithFilters,
    getCurrentFilters,
    handleLocationChange,
    handleOnlineBookingFilter,
    handleExactCapacityFilter,
    handleRemoveFilter,
    updateFilters,
    hasFiltersApplied,
    handlePageFilter,
  }
}
